import { observer } from 'mobx-react';
import * as React from 'react';
import { Button, ErrorMessage, FlexRow, Footer, Grid, BodyText, NavigationItem, WorkflowHeader } from 'ui-toolkit';
import { IPolicyJacketUI } from '../classes/PolicyJacketUI';
import { IPolicyOrderDetails } from '../classes/PolicyOrderDetails';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { IRecordedDeedRequired, IRecordedDeed } from '../classes/RecordedDeed';
import { IRecordedMortgageRequired, IRecordedMortgage } from '../classes/RecordedMortgage';
import { IUnderwriterDataUI } from '../classes/UnderwriterDataUI';
import AddPolicyDetails from '../components/AddPolicyDetails';
import Endorsements from '../components/Endorsements';
import OrderDetails from '../components/OrderDetails';
import RecordedDeedDetails from '../components/RecordedDeedDetails';
import RecordedMortgageDetails from '../components/RecordedMortgageDetails';
import ScheduleAButtonBlock from '../components/ScheduleAButtonBlock';
import getText from '../utils/getText';
import { eSingleFields } from '../context/state/eSingleFields';
import { AmericanDigitalDetails } from '../components/AmericanDigitalDetails';
import { IPolicyNumberError } from '../classes/IPolicyNumberError';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';
import { RouteComponentProps } from 'react-router';
import IRouterParams from '../IRouterParams';
import { eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';
import GrayLine from '../../components/shared/GrayLine';

interface IPolicyScheduleAProps extends RouteComponentProps<IRouterParams> {
  policyType: PolicyTypeEnum;
  orderId: string;
  action: string;
  title: string;
  showEndorsements: boolean;
  showMortgageeClause: boolean;
  cdBuilderPageURL: string;
  editOrderUrl: string;
  editAmtOfInsuredFieldName: eSingleFields;
  editAddressUrl: string;
  editBorrowerPage: string;
  errorMessage: string | IPolicyNumberError;
  getEndorsements: string[];
  getErrorRecordedDeed: object;
  getErrorRecordedMortgage: object;
  getOrderDetails: IPolicyOrderDetails;
  getPolicyJacket: IPolicyJacketUI;
  getRecordedDeed: IRecordedDeed;
  recordedDeedRequired: IRecordedDeedRequired;
  recordedMortgageRequired: IRecordedMortgageRequired;
  getRecordedMortgage: IRecordedMortgage;
  getUserRecordedMortgage: IRecordedMortgage;
  getUserRecordedDeed: IRecordedDeed;
  goOtherRoute: (nextPath: string) => void;
  loadingPage: boolean;
  loadedPage: boolean;
  onCancelRecordedDeed: () => void;
  onCancelRecordedMortgage: () => void;
  onChangePolicyNumber: (newValue: string | null, error?: string | null) => void;
  onPolicyNumberBlur: () => void;
  onDeletePolicyJacket: () => void;
  onJacketUpload: (files: FileList | null) => void;
  onUpdatePage: () => void;
  onUpdateRecordedDeed: () => void;
  onUpdateRecordedMortgage: () => void;
  policyJacketError: object;
  recordedDeedWasChangedWithoutError: boolean;
  recordedMortgageWasChangedWithoutError: boolean;
  savePolicy: () => Promise<void>;
  policyFetchedSuccess: boolean;
  policyNumberRequired: boolean;
  setUserRecordedDeed: (recordedDeed: IRecordedDeed, propName: string, error?: string | null) => void;
  setUserRecordedMortgage: (mortgage: IRecordedMortgage, propName: string, error?: string | null) => void;
  updateInProcess: boolean;
  saveSuccess: boolean;
  updatePageInProcess: boolean;
  updatedPage: boolean;
  underwriter: IUnderwriterDataUI;
  disableNextButton: boolean;
  isAmericanDigital: boolean;
}

class PolicyScheduleA extends React.Component<IPolicyScheduleAProps, {}> {
  public static defaultProps: Partial<IPolicyScheduleAProps> = {};
  private getPageError = (): React.ReactNode | null => {
    if (this.props.errorMessage) {
      return typeof this.props.errorMessage === 'string' ? (
        this.props.errorMessage
      ) : (
        <>
          {this.props.errorMessage.orderIdWithExistingPolicy ? (
            <BodyText>
              This policy ID has already been taken. See order this has been associated with{' '}
              <NavigationItem
                isInline
                text={'here'}
                to={clientUrls.urlFor.orderStatus(this.props.errorMessage.orderIdWithExistingPolicy)}
              />
            </BodyText>
          ) : (
            this.props.errorMessage
          )}
        </>
      );
    } else return null;
  };

  public render() {
    return (
      <div className="App policy">
        <div>
          <WorkflowHeader title={this.props.title} onHomeClick={this.goToHomePage} onBackClick={this.goBack}>
            <ScheduleAButtonBlock
              policyType={this.props.policyType}
              showFetching={this.props.updateInProcess}
              showSuccess={this.props.saveSuccess}
              disableButton={this.props.disableNextButton}
              goNext={this.goNext}
              goBack={this.goBack}
            />
          </WorkflowHeader>
          <div className={'page-content'}>
            {!this.getPageError() ? null : <ErrorMessage message={this.getPageError()} />}
            {this.props.loadingPage ? null : (
              <Grid columnCount={2}>
                <div>
                  <AddPolicyDetails
                    title={getText(`policy.policyJacketTitle.${this.props.policyType}`)}
                    subTitle={getText(`policy.policyJacketSubtitle.${this.props.policyType}`)}
                    policyJacketLabel={getText(`policy.policyJacketLabel.${this.props.policyType}`)}
                    policyJacketText={getText(
                      `policy.policyJacketText.${this.props.policyType}.${
                        this.props.isAmericanDigital ? 'americanDigital' : 'default'
                      }`
                    )}
                    error={this.props.policyJacketError}
                    underwriter={this.props.underwriter}
                    policyJacket={this.props.getPolicyJacket}
                    onJacketUpload={this.props.onJacketUpload}
                    onDeletePolicyJacket={this.props.onDeletePolicyJacket}
                    onPolicyChange={this.props.onChangePolicyNumber}
                    fetchedSuccess={this.props.policyFetchedSuccess}
                    policyNumberRequired={this.props.policyNumberRequired}
                    onPolicyNumberBlur={this.props.onPolicyNumberBlur}
                    isAmericanDigital={this.props.isAmericanDigital}
                  />
                </div>
                <Grid>
                  <OrderDetails
                    data={this.props.getOrderDetails}
                    policyType={this.props.policyType}
                    cdBuilderPage={this.props.cdBuilderPageURL}
                    editOrderPage={this.props.editOrderUrl}
                    editAmtOfInsuredFieldName={this.props.editAmtOfInsuredFieldName}
                    editAddressUrl={this.props.editAddressUrl}
                    editBorrowerPage={this.props.editBorrowerPage}
                    errorMessage={!!this.props.errorMessage}
                    showSaveSuccess={this.props.updatedPage}
                    showSaveSpinner={this.props.updatePageInProcess}
                    onUpdatePageClick={this.props.onUpdatePage}
                    showMortgageeClause={this.props.showMortgageeClause}
                    orderId={this.props.orderId}
                  />
                  {this.props.isAmericanDigital ? (
                    <AmericanDigitalDetails
                      policyType={this.props.policyType}
                      recordedDeed={this.props.getRecordedDeed}
                      recordedMortgage={this.props.getRecordedMortgage}
                      transactionType={this.props.getOrderDetails.transactionCategory || eTransactionCategory.Refinance}
                      vestedIn={this.props.getOrderDetails.vestedIn}
                      userData={this.props.getUserRecordedMortgage}
                      requiredFields={this.props.recordedMortgageRequired}
                      onRecordedMortgageChange={this.props.setUserRecordedMortgage}
                      errorRecordedMortgage={this.props.getErrorRecordedMortgage}
                      onUpdateDocument={this.props.onUpdateRecordedMortgage}
                      onCancelClick={this.props.onCancelRecordedMortgage}
                      showSaveSpinner={this.props.updateInProcess}
                      showSaveSuccess={this.props.saveSuccess}
                      updateErrorText={this.props.errorMessage.toString()}
                      recordedMortgageWasChanged={this.props.recordedMortgageWasChangedWithoutError}
                    />
                  ) : (
                    <>
                      {this.props.policyType !== PolicyTypeEnum.shortForm ? (
                        <React.Fragment>
                          <GrayLine />
                          <RecordedDeedDetails
                            data={this.props.getRecordedDeed}
                            requiredFields={this.props.recordedDeedRequired}
                            errorRecordedDeed={this.props.getErrorRecordedDeed}
                            userData={this.props.getUserRecordedDeed}
                            recordedDeedReadyForSave={this.props.recordedDeedWasChangedWithoutError}
                            showSaveSpinner={this.props.updateInProcess}
                            showSaveSuccess={this.props.saveSuccess}
                            updateErrorText={this.props.errorMessage.toString()}
                            onUpdateDocument={this.props.onUpdateRecordedDeed}
                            onCancelClick={this.props.onCancelRecordedDeed}
                            onRecordedDeedChange={this.props.setUserRecordedDeed}
                          />
                        </React.Fragment>
                      ) : null}
                      {this.props.policyType !== PolicyTypeEnum.owner ? (
                        <>
                          <GrayLine />
                          <RecordedMortgageDetails
                            data={this.props.getRecordedMortgage}
                            title={getText('recordedMortgage.new')}
                            modalWindowTitle={getText('recordedMortgage.editNew')}
                            userData={this.props.getUserRecordedMortgage}
                            requiredFields={this.props.recordedMortgageRequired}
                            onRecordedMortgageChange={this.props.setUserRecordedMortgage}
                            errorRecordedMortgage={this.props.getErrorRecordedMortgage}
                            onUpdateDocument={this.props.onUpdateRecordedMortgage}
                            onCancelClick={this.props.onCancelRecordedMortgage}
                            showSaveSpinner={this.props.updateInProcess}
                            showSaveSuccess={this.props.saveSuccess}
                            updateErrorText={this.props.errorMessage.toString()}
                            recordedMortgageWasChanged={this.props.recordedMortgageWasChangedWithoutError}
                          />
                        </>
                      ) : null}
                    </>
                  )}
                  {this.props.showEndorsements ? (
                    <>
                      <GrayLine />
                      <Endorsements
                        data={this.props.getEndorsements}
                        cdBuilderPage={this.props.cdBuilderPageURL}
                        errorMessage={!!this.props.errorMessage}
                        showSaveSuccess={this.props.loadedPage}
                        showSaveSpinner={this.props.updatePageInProcess}
                        onUpdatePageClick={this.props.onUpdatePage}
                      />
                    </>
                  ) : null}
                </Grid>
              </Grid>
            )}
          </div>
        </div>
        <Footer>
          <FlexRow justifyContent={'space-between'} width={100}>
            <Button intent="primary" onClick={this.goBack}>
              Back
            </Button>
            <ScheduleAButtonBlock
              policyType={this.props.policyType}
              additionalText={'Next: Schedule B'}
              showFetching={this.props.updateInProcess}
              showSuccess={this.props.saveSuccess}
              disableButton={this.props.disableNextButton}
              goNext={this.goNext}
              goBack={this.goBack}
            />
          </FlexRow>
        </Footer>
      </div>
    );
  }

  private goBack = () => {
    this.props.history.push(clientUrls.urlFor.title(this.props.orderId));
  };

  private goToHomePage = () => {
    this.props.history.push(clientUrls.urlFor.adminOrders());
  };

  private goNext = async () => {
    if (!this.props.disableNextButton) {
      try {
        await this.props.savePolicy();
        this.props.goOtherRoute(
          clientUrls.urlFor.policyGenerationB(
            this.props.orderId,
            this.props.policyType,
            this.props.action as 'edit' | 'create'
          )
        );
      } catch (e: any) {
        // error
      }
    }
  };
}

export default observer(PolicyScheduleA);
